import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { usePersona } from "../../persona-context";
import { ProcessNextHint, ProcessStepper } from "../../process-stepper";
import { type DossierStatusId, statusById } from "../../status";

// ── Real dossier data: SHW_2026_100_1 ──────────────────────────────────────

const DOSSIER = {
  ref: "EA-2026-041",
  project: "Language preferences for ADHD people / people with ADHD",
  subproject: "R2D2-MH · Horizon Europe",
  researcher: "Sarah Claeys",
  researcherInst: "Centrum voor Ethiek · Universiteit Antwerpen",
  seniorResearcher: "Dr. Lisa Vandenberghe (neurodivergent, Centrum voor Ethiek)",
  promotor: "Prof. dr. Petra Vereecken",
  faculty: "Centrum voor Ethiek",
  submitted: "9 april 2026",
  funding: "Horizon Europe (101057385) · UKRI (10039383) · SERI (22.00277)",
  status: "revisie-gevraagd" as DossierStatusId,
  completeness: 91,
};

const SMART_REVISION_ITEMS = [
  {
    source: "Reviewer",
    section: "Inleidende verklaring",
    title: "Benoem waarom de ADHD-stigmameting ethisch laag risico blijft",
    original:
      "Ik ben er zeker van dat mijn aanvraag zeer nauwkeurig en conform de richtlijnen van de EASHW werd opgesteld. Mijn aanvraag omvat daarnaast geen (zeer?) gevoelige thema's of een kwetsbaar doelpubliek en ik heb geen verdere vragen voor de commissie.",
    feedback:
      "De aanvraag is inhoudelijk aanvaardbaar, maar de verklaring over gevoelige thema's moet overeenkomen met de stigmameting in de vragenlijst.",
    proposed:
      "Licht toe dat de stigmameting anoniem verloopt, geen identificerende gegevens verzamelt en dat deelnemers vooraf duidelijke informatie krijgen over de aard van de vragen.",
  },
  {
    source: "Bureau",
    section: "Luik C, vraag 10",
    title: "Voeg een korte motivering en ondersteuning toe",
    original: "Antwoord: Nee. Opmerkingen: (geen)",
    feedback:
      "Het bureau vraagt geen nieuwe inhoudelijke review als de aanvraag kort verduidelijkt hoe mogelijke emotionele belasting wordt opgevangen.",
    proposed:
      "Voeg toe dat deelnemers vragen mogen overslaan, op elk moment kunnen stoppen en in de informatiebrief worden verwezen naar passende ondersteuning.",
  },
  {
    source: "Secretariaat",
    section: "Luik B, vraag 2",
    title: "Maak de GDPR-bevestiging voor ADHD Europe concreet",
    original:
      "Ik bevestig dat het privacybeleid en de werkwijze van ADHD Europe voldoen aan de AVG/GDPR-wetgeving.",
    feedback:
      "De standaardzin volstaat bijna, maar verwijs expliciet naar de privacy policy of bevestig dat de verwerkersafspraken gecontroleerd zijn.",
    proposed:
      "Bevestig dat de privacy policy van ADHD Europe werd nagekeken en dat de rekrutering enkel via hun eigen communicatiekanalen verloopt.",
  },
];

// ── Main component ────────────────────────────────────────────────────────────

export function AanvraagDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { persona } = usePersona();
  const readOnly = persona === "promoter";
  const [acceptedRevision, setAcceptedRevision] = useState(false);
  const currentStatus: DossierStatusId = acceptedRevision ? "aanpassingen-akkoord" : DOSSIER.status;

  const dossierId = searchParams.get("dossierId") ?? DOSSIER.ref;

  const backPath =
    persona === "backoffice"
      ? "/dashboard/dossiers"
      : persona === "promoter"
        ? "/dashboard/team-aanvragen"
        : "/dashboard/mijn-aanvragen";

  return (
    <section>
      {/* ── Header: dossier identity + key facts, combined ── */}
      <div className="card ua-mb-space">
        <div
          className="card__body"
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <div>
              <div className="eyebrow">Dossier {dossierId}</div>
              <h1 style={{ fontSize: "20px", lineHeight: 1.3, maxWidth: "600px" }}>
                {DOSSIER.project}
              </h1>
              <p style={{ marginTop: "4px" }}>{DOSSIER.subproject}</p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
              {readOnly && <span className="badge badge--info">Alleen-lezen (promotor)</span>}
              <button className="btn btn--secondary" onClick={() => void navigate(backPath)}>
                ← Terug
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "28px",
              flexWrap: "wrap",
              paddingTop: "16px",
              borderTop: "1px solid var(--ua-border)",
            }}
          >
            <div>
              <div className="ua-muted-xs">Onderzoeker</div>
              <div className="ua-bold-sm">{DOSSIER.researcher}</div>
              <div className="ua-muted-xs">{DOSSIER.researcherInst}</div>
            </div>
            <div>
              <div className="ua-muted-xs">Promotor</div>
              <div className="ua-bold-sm">{DOSSIER.promotor}</div>
            </div>
            <div>
              <div className="ua-muted-xs">Ingediend</div>
              <div className="ua-bold-sm">{DOSSIER.submitted}</div>
            </div>
            <div style={{ maxWidth: "340px" }}>
              <div className="ua-muted-xs">Financiering</div>
              <div className="ua-bold-sm">{DOSSIER.funding}</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Process stepper: "you are here" ── */}
      <div className="card ua-mb-space">
        <div className="card__body">
          <ProcessStepper currentStageId={statusById(currentStatus).stage} showActor={false} />
          {currentStatus === "revisie-gevraagd" ? (
            <div className="process-next">
              <span className="process-next__label">Wat volgt?</span>
              <span className="process-next__text">
                Controleer de beperkte feedback van reviewer en bureau. Als u akkoord gaat met de
                voorgestelde verduidelijkingen, wordt het dossier meteen aanvaard.
              </span>
            </div>
          ) : (
            statusById(currentStatus).stage && (
              <ProcessNextHint currentStageId={statusById(currentStatus).stage!} />
            )
          )}
        </div>
      </div>

      <SmartRevisionPanel
        accepted={acceptedRevision}
        readOnly={readOnly}
        onAccept={() => setAcceptedRevision(true)}
      />
    </section>
  );
}

function SmartRevisionPanel({
  accepted,
  readOnly,
  onAccept,
}: {
  accepted: boolean;
  readOnly: boolean;
  onAccept: () => void;
}) {
  const [confirmedTitles, setConfirmedTitles] = useState<Set<string>>(new Set());
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(SMART_REVISION_ITEMS.map((item) => [item.title, ""])),
  );
  const confirmedCount = confirmedTitles.size;
  const allConfirmed = confirmedCount === SMART_REVISION_ITEMS.length;

  const confirmItem = (title: string) => {
    if (!(drafts[title] ?? "").trim()) {
      return;
    }
    setConfirmedTitles((prev) => new Set(prev).add(title));
  };

  const editDraft = (title: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [title]: value }));
  };

  return (
    <div className="card ua-mb-space">
      <div className="card__head">
        <div>
          <div className="eyebrow" style={{ marginBottom: "2px" }}>
            Stap 6 · afronding na feedback
          </div>
          <h2>Slimme revisie: feedback verwerken</h2>
        </div>
        {!accepted && (
          <span
            style={{
              fontSize: "11px",
              fontWeight: 700,
              color: allConfirmed ? "var(--ua-success)" : "var(--ua-navy)",
              background: allConfirmed ? "#edfaf2" : "#eef2f9",
              border: `1px solid ${allConfirmed ? "#a3e4c1" : "#c5d0e8"}`,
              borderRadius: "999px",
              padding: "2px 9px",
              whiteSpace: "nowrap",
            }}
          >
            ✦ {confirmedCount}/{SMART_REVISION_ITEMS.length} reflecties bevestigd
          </span>
        )}
      </div>
      <div className="card__body">
        <div className="info-banner" style={{ marginBottom: "16px" }}>
          <span className="info-banner__icon" style={{ fontWeight: 700 }}>
            i
          </span>
          <div>
            <strong>Reviewer en bureau hebben beperkte feedback gegeven.</strong> Verwerk elk punt
            in uw eigen woorden. De AI-tip boven het invoerveld is een hulpmiddel, geen
            kant-en-klaar antwoord.
          </div>
        </div>

        <div style={{ display: "grid", gap: "12px" }}>
          {SMART_REVISION_ITEMS.map((item, index) => {
            const isConfirmed = accepted || confirmedTitles.has(item.title);
            return (
              <div
                key={item.title}
                style={{
                  border: "1px solid var(--ua-border)",
                  padding: "14px 16px",
                  background: isConfirmed ? "rgba(46, 139, 87, 0.05)" : "var(--ua-surface)",
                }}
              >
                <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                  <span className={`badge badge--${isConfirmed ? "success" : "info"}`}>
                    {isConfirmed ? "✓" : index + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div className="ua-muted-xs">{item.section}</div>
                    <div style={{ fontWeight: 700, marginTop: "2px" }}>{item.title}</div>
                    <p style={{ margin: "6px 0 0", fontSize: "13px", color: "var(--ua-muted)" }}>
                      {item.feedback}
                    </p>

                    <div style={{ marginTop: "10px" }}>
                      <div className="ua-muted-xs" style={{ marginBottom: "4px" }}>
                        Oorspronkelijk antwoord
                      </div>
                      <div
                        style={{
                          padding: "8px 12px",
                          background: "var(--ua-bg)",
                          borderLeft: "3px solid var(--ua-border)",
                          fontSize: "13px",
                          color: "var(--ua-muted)",
                          fontStyle: "italic",
                        }}
                      >
                        {item.original}
                      </div>
                    </div>

                    <div style={{ marginTop: "10px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "8px",
                          padding: "8px 12px",
                          background: "#fffaf0",
                          border: "1px solid #f0dfa8",
                          borderRadius: "4px",
                        }}
                      >
                        <span style={{ fontSize: "10px", fontWeight: 700, color: "#8a6d1f" }}>
                          ✦ TIP
                        </span>
                        <span style={{ fontSize: "13px", color: "#6b5416" }}>{item.proposed}</span>
                      </div>
                    </div>

                    <div style={{ marginTop: "10px" }}>
                      <div className="ua-muted-xs" style={{ marginBottom: "4px" }}>
                        Uw reflectie
                      </div>
                      <textarea
                        value={drafts[item.title] ?? ""}
                        onChange={(e) => editDraft(item.title, e.target.value)}
                        readOnly={readOnly || isConfirmed}
                        rows={3}
                        placeholder="Leeg. Reflecteer hier in uw eigen woorden; gebruik de tip hierboven als hulp."
                        style={{
                          width: "100%",
                          padding: "10px 12px",
                          background: isConfirmed ? "var(--ua-bg)" : "#fff",
                          border: "1px solid var(--ua-border)",
                          borderLeft: "3px solid var(--ua-navy)",
                          borderRadius: "4px",
                          fontSize: "13px",
                          fontFamily: "inherit",
                          lineHeight: 1.5,
                          resize: "vertical",
                          color: "inherit",
                        }}
                      />
                    </div>
                    {!readOnly && !accepted && (
                      <div style={{ marginTop: "10px" }}>
                        {isConfirmed ? (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "var(--ua-success)",
                              fontWeight: 700,
                            }}
                          >
                            ✓ Reflectie bevestigd
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => confirmItem(item.title)}
                            disabled={!(drafts[item.title] ?? "").trim()}
                            style={{
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "var(--ua-navy)",
                              background: "none",
                              border: "1px solid #c5d0e8",
                              borderRadius: "999px",
                              padding: "3px 12px",
                              cursor: (drafts[item.title] ?? "").trim() ? "pointer" : "not-allowed",
                              fontFamily: "inherit",
                              opacity: (drafts[item.title] ?? "").trim() ? 1 : 0.5,
                            }}
                          >
                            ✓ Bevestig mijn reflectie
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!readOnly && (
          <div
            style={{
              marginTop: "16px",
              paddingTop: "16px",
              borderTop: "1px solid var(--ua-border)",
              display: "flex",
              gap: "12px",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <div style={{ maxWidth: "620px" }}>
              <div style={{ fontWeight: 700 }}>
                {accepted
                  ? "Akkoord geregistreerd"
                  : allConfirmed
                    ? "Ga akkoord met deze beperkte aanpassingen"
                    : "Bevestig eerst al uw reflecties hierboven"}
              </div>
              <div className="ua-muted-sm">
                {accepted
                  ? "Het dossier is nu aanvaard en kan worden afgesloten."
                  : "Dit volgt dezelfde logica als een nieuwe aanvraag: u bevestigt elke reflectie apart en dient de bijgewerkte versie in."}
              </div>
            </div>
            <button
              className="btn btn--primary"
              onClick={onAccept}
              disabled={accepted || !allConfirmed}
            >
              {accepted ? "Aanvaard" : "Accepteer en rond af"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
